import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSyncAlt, FaArrowUp, FaArrowDown, FaPhone, FaBars } from 'react-icons/fa';
import { get_api, put_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import AudioPlayer from '../students/student_profile/CallAudioPlayer';
import MytNotification from '../../../myt_chat/notification';

const STATUS_STYLES = {
  completed: { color: '#166534', background: '#dcfce7' },
  'in-progress': { color: '#166534', background: '#dcfce7' },
  answered: { color: '#166534', background: '#dcfce7' },
  ringing: { color: '#92400e', background: '#fef3c7' },
  initiated: { color: '#92400e', background: '#fef3c7' },
  busy: { color: '#991b1b', background: '#fee2e2' },
  failed: { color: '#991b1b', background: '#fee2e2' },
  'no-answer': { color: '#991b1b', background: '#fee2e2' },
  'not-answered': { color: '#991b1b', background: '#fee2e2' },
  canceled: { color: '#374151', background: '#e5e7eb' }
};

function StatusPill({ status, answered }) {
  let display = status;
  if (status === 'completed' && !answered) display = 'not-answered';
  const style = STATUS_STYLES[display] ||
    STATUS_STYLES[status] || { color: '#374151', background: '#e5e7eb' };
  return (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
        textTransform: 'capitalize'
      }}
    >
      {display || '—'}
    </span>
  );
}

const formatSeconds = (secs) => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const BASE_POLL_MS = 60 * 1000;
const JITTER_MIN_MS = 4000;
const JITTER_MAX_MS = 8000;

const nextPollDelay = () =>
  BASE_POLL_MS + Math.floor(JITTER_MIN_MS + Math.random() * (JITTER_MAX_MS - JITTER_MIN_MS));

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

const formatDuration = (startedAt, now) => {
  if (!startedAt) return '--:--';
  const start = new Date(startedAt).getTime();
  const diff = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

function TwilioAgent() {
  const currentUserUUID = useSelector((state) => state.home.cms_teacher_uuid);

  const [available, setAvailable] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [ongoingCalls, setOngoingCalls] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);

  const defaultFrom = useMemo(() => moment().subtract(7, 'days').format('YYYY-MM-DD'), []);
  const defaultTo = useMemo(() => moment().format('YYYY-MM-DD'), []);
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const [notificationPermission, setNotificationPermission] = useState(() =>
    MytNotification.getPermissionState()
  );
  const [notificationBannerDismissed, setNotificationBannerDismissed] = useState(false);

  const refreshNotificationPermission = useCallback(() => {
    setNotificationPermission(MytNotification.getPermissionState());
  }, []);

  useEffect(() => {
    MytNotification.webPushInit();
    refreshNotificationPermission();

    const onVis = () => refreshNotificationPermission();
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshNotificationPermission]);

  const requestNotificationPermission = async () => {
    const perm = await MytNotification.ensurePermission({ userGesture: true });
    setNotificationPermission(perm);
    setNotificationBannerDismissed(false);
    if (perm === 'granted') {
      MytNotification.webPushInit();
    }
  };

  const sortedRecentCalls = useMemo(() => {
    const copy = [...recentCalls];
    copy.sort((a, b) => {
      const ta = new Date(a.created_at).getTime() || 0;
      const tb = new Date(b.created_at).getTime() || 0;
      return sortDir === 'asc' ? ta - tb : tb - ta;
    });
    return copy;
  }, [recentCalls, sortDir]);

  const toggleSort = () => setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));

  const fetchRecentCalls = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const url = `${getUrl('/v2/twilio_call/recent_calls')}?from=${encodeURIComponent(
        fromDate
      )}&to=${encodeURIComponent(toDate)}`;
      const resp = await get_api(url, true);
      setRecentCalls(resp.data?.data?.calls || []);
    } catch (e) {
      console.error('twilio_agent.recent_calls.fetch_failed', e);
      setRecentError('Failed to load recent calls');
    } finally {
      setRecentLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchRecentCalls();
  }, [fetchRecentCalls]);

  const fetchAvailability = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
      const next = !!resp.data?.data?.available;
      setAvailable(next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', { detail: { available: next } })
      );
    } catch (e) {
      console.error('twilio_agent.availability.fetch_failed', e);
    }
  }, []);

  const fetchOngoingCalls = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/ongoing_calls'), true);
      setOngoingCalls(resp.data?.data?.calls || []);
    } catch (e) {
      console.error('twilio_agent.ongoing_calls.fetch_failed', e);
    }
  }, []);

  const fetchAvailableAgents = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/available_agents'), true);
      setAvailableAgents(resp.data?.data?.agents || []);
    } catch (e) {
      console.error('twilio_agent.available_agents.fetch_failed', e);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setResyncing(true);
    try {
      await Promise.all([fetchAvailability(), fetchOngoingCalls(), fetchAvailableAgents()]);
    } finally {
      setLoading(false);
      setResyncing(false);
    }
  }, [fetchAvailability, fetchOngoingCalls, fetchAvailableAgents]);

  useEffect(() => {
    fetchAll();

    const timers = [];
    const schedule = (fn) => {
      const run = async () => {
        await fn();
        timers.push(setTimeout(run, nextPollDelay()));
      };
      timers.push(setTimeout(run, nextPollDelay()));
    };
    schedule(fetchAvailability);
    schedule(fetchOngoingCalls);
    schedule(fetchAvailableAgents);

    const tickId = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(tickId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh Ongoing calls immediately when an incoming call arrives or ends.
  // GlobalIncomingCallManager dispatches this on relay arrival, pill-accept,
  // disconnect, cancel, reject, or error. We also refetch 2 s later to catch
  // cases where Twilio's Status webhook reaches our backend slightly after
  // the local pill transition.
  useEffect(() => {
    const timers = [];
    const onRefresh = () => {
      fetchOngoingCalls();
      timers.push(setTimeout(() => fetchOngoingCalls(), 2000));
    };
    window.addEventListener('twilio-ongoing-calls-refresh', onRefresh);
    return () => {
      window.removeEventListener('twilio-ongoing-calls-refresh', onRefresh);
      timers.forEach(clearTimeout);
    };
  }, [fetchOngoingCalls]);

  const toggleAvailability = async () => {
    if (toggling) return;
    const next = !available;
    setToggling(true);
    setAvailable(next);
    window.dispatchEvent(
      new CustomEvent('twilio-agent-availability-changed', { detail: { available: next } })
    );
    try {
      await put_api(getUrl('/v2/twilio_call/availability'), { available: next }, true);
      fetchAll();
    } catch (e) {
      console.error('twilio_agent.toggle_failed', e);
      setAvailable(!next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', { detail: { available: !next } })
      );
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="p-6">
      <style>{`
        @keyframes twilio-agent-vibrate {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-15deg); }
          40% { transform: rotate(15deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(10deg); }
        }
        .twilio-agent-vibrate-btn svg {
          animation: twilio-agent-vibrate 0.8s infinite;
        }
      `}</style>

      {!notificationBannerDismissed &&
        notificationPermission !== 'granted' &&
        notificationPermission !== 'unsupported' && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              background: notificationPermission === 'denied' ? '#fef2f2' : '#fff7ed',
              borderColor: notificationPermission === 'denied' ? '#fecaca' : '#fed7aa',
              color: notificationPermission === 'denied' ? '#991b1b' : '#9a3412'
            }}
          >
            <div style={{ fontSize: 13 }}>
              {notificationPermission === 'default'
                ? 'Enable browser notifications so you don’t miss incoming calls.'
                : 'Browser notifications are blocked. Re-enable them in your browser site settings.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {notificationPermission === 'default' && (
                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  style={{
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Enable notifications
                </button>
              )}
              <button
                type="button"
                onClick={() => setNotificationBannerDismissed(true)}
                style={{
                  background: 'transparent',
                  color: 'inherit',
                  border: '1px solid currentColor',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Twilio Agent</h1>
          <button
            type="button"
            onClick={fetchAll}
            disabled={resyncing}
            aria-label="Refresh"
            title="Refresh"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              width: 36,
              borderRadius: 9999,
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: resyncing ? 'wait' : 'pointer',
              opacity: resyncing ? 0.7 : 1,
              padding: 0
            }}
          >
            <FaSyncAlt size={16} color="#f59e0b" className={resyncing ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-end leading-tight">
            <span
              className={`text-sm font-semibold ${available ? 'text-orange-600' : 'text-gray-500'}`}
            >
              {available ? 'Available' : 'Unavailable'}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              {available ? 'Receiving calls' : 'Not receiving calls'}
            </span>
          </div>
          <button
            type="button"
            onClick={toggleAvailability}
            disabled={toggling}
            role="switch"
            aria-checked={available}
            aria-label="Toggle availability"
            className={`relative inline-flex h-9 w-20 shrink-0 items-center rounded-full p-1 transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              available
                ? 'bg-orange-500 focus-visible:ring-orange-500'
                : 'bg-gray-300 focus-visible:ring-gray-400'
            } ${toggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
          >
            <span
              aria-hidden="true"
              className={`absolute text-[11px] font-semibold uppercase tracking-wider transition-opacity duration-200 ${
                available ? 'left-3 text-white opacity-100' : 'left-3 text-white opacity-0'
              }`}
            >
              On
            </span>
            <span
              aria-hidden="true"
              className={`absolute text-[11px] font-semibold uppercase tracking-wider transition-opacity duration-200 ${
                available ? 'right-3 text-gray-600 opacity-0' : 'right-3 text-gray-600 opacity-100'
              }`}
            >
              Off
            </span>
            <span
              aria-hidden="true"
              className={`pointer-events-none relative inline-block h-7 w-7 transform rounded-full bg-white shadow-md transition-transform duration-300 ease-in-out ${
                available ? 'translate-x-11' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <section className="col-span-12 lg:col-span-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold">Ongoing calls</h2>
            <span className="text-xs text-gray-500">{ongoingCalls.length} active</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">Student</th>
                  <th className="text-left px-5 py-2 font-medium">Care agent</th>
                  <th className="text-left px-5 py-2 font-medium">Duration</th>
                  <th className="text-left px-5 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : ongoingCalls.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-gray-500">
                      No ongoing calls
                    </td>
                  </tr>
                ) : (
                  ongoingCalls.map((call) => {
                    const isIncomingRinging =
                      (call.direction || '').toLowerCase() === 'incoming' && !call.answered;
                    const callerLabel =
                      call.student_first_name || call.from_number || 'Incoming caller';
                    const agentLabel = call.care_agent_name || call.assigned_agent_name || '--';
                    return (
                      <tr key={call.uuid} className="border-t border-gray-100">
                        <td className="text-left px-5 py-3">{callerLabel}</td>
                        <td className="text-left px-5 py-3">{agentLabel}</td>
                        <td className="text-left px-5 py-3 font-mono text-gray-800">
                          {formatDuration(call.started_at, now)}
                        </td>
                        <td className="text-left px-5 py-3">
                          {isIncomingRinging ? (
                            <span
                              aria-label="Incoming call ringing"
                              title="Incoming call ringing — the pill will open automatically when Twilio reaches your browser"
                              className="twilio-agent-vibrate-btn"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: 9999,
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                color: '#b45309'
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-flex',
                                  transform: 'rotate(90deg)'
                                }}
                              >
                                <FaPhone size={14} />
                              </span>
                            </span>
                          ) : call.answered ? (
                            <button
                              type="button"
                              aria-label="Call actions"
                              title="Call actions"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: 9999,
                                background: '#f3f4f6',
                                border: '1px solid #e5e7eb',
                                color: '#374151',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              <FaBars size={14} />
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold">Available agents</h2>
            <span className="text-xs text-gray-500">{availableAgents.length}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {loading ? (
              <li className="px-5 py-6 text-center text-gray-500">Loading...</li>
            ) : availableAgents.length === 0 ? (
              <li className="px-5 py-6 text-center text-gray-500">No agents available</li>
            ) : (
              availableAgents.map((agent) => {
                const isSelf = agent.uuid === currentUserUUID;
                return (
                  <li key={agent.uuid} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                      <span className="text-sm text-gray-800">
                        {agent.name || agent.email || agent.uuid}
                      </span>
                    </div>
                    {isSelf && <span className="text-xs text-orange-600 font-medium">(You)</span>}
                  </li>
                );
              })
            )}
          </ul>
        </aside>
      </div>

      <section className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold">Recent calls</h2>
            <span className="text-xs text-gray-500">
              {recentCalls.length} result{recentCalls.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1 text-xs text-gray-600">
              From
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-gray-600">
              To
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={moment().format('YYYY-MM-DD')}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={fetchRecentCalls}
              disabled={recentLoading}
              aria-label="Refresh recent calls"
              title="Refresh recent calls"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 32,
                width: 32,
                borderRadius: 9999,
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                cursor: recentLoading ? 'wait' : 'pointer',
                opacity: recentLoading ? 0.7 : 1,
                padding: 0
              }}
            >
              <FaSyncAlt
                size={14}
                color="#f59e0b"
                className={recentLoading ? 'animate-spin' : ''}
              />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th
                  onClick={toggleSort}
                  className="text-left px-5 py-2 font-medium cursor-pointer select-none"
                  title="Sort by date"
                >
                  Date &amp; time
                  {sortDir === 'asc' ? (
                    <FaArrowUp
                      size={11}
                      color="#f59e0b"
                      style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }}
                    />
                  ) : (
                    <FaArrowDown
                      size={11}
                      color="#f59e0b"
                      style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }}
                    />
                  )}
                </th>
                <th className="text-left px-5 py-2 font-medium">Student</th>
                <th className="text-left px-5 py-2 font-medium">Care agent</th>
                <th className="text-left px-5 py-2 font-medium">Status</th>
                <th className="text-left px-5 py-2 font-medium">Duration</th>
                <th className="text-left px-5 py-2 font-medium">Direction</th>
                <th className="text-left px-5 py-2 font-medium">Recording</th>
              </tr>
            </thead>
            <tbody>
              {recentLoading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : recentError ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-red-600">
                    {recentError}
                  </td>
                </tr>
              ) : recentCalls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-gray-500">
                    No calls in this range
                  </td>
                </tr>
              ) : (
                sortedRecentCalls.map((call) => {
                  const dir = (call.direction || 'outgoing').toLowerCase();
                  const dirLabel = dir === 'incoming' ? 'IN' : 'OUT';
                  return (
                    <tr key={call.id} className="border-t border-gray-100">
                      <td className="text-left px-5 py-3 whitespace-nowrap">
                        {moment(call.created_at).format('lll')}
                      </td>
                      <td className="text-left px-5 py-3">
                        {call.student_uuid ? (
                          <Link
                            to={`/studentprofile?id=${encodeURIComponent(call.student_uuid)}`}
                            className="text-blue-600 hover:underline"
                          >
                            {call.student_first_name || call.student_uuid}
                          </Link>
                        ) : (
                          call.student_first_name || call.from_number || '—'
                        )}
                      </td>
                      <td className="text-left px-5 py-3">{call.care_agent_name || '—'}</td>
                      <td className="text-left px-5 py-3">
                        <StatusPill status={call.status} answered={!!call.answered} />
                        {call.call_type === 'voicemail' && (
                          <span
                            style={{
                              marginLeft: 6,
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 9999,
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#1e3a8a',
                              background: '#dbeafe'
                            }}
                          >
                            Voicemail
                          </span>
                        )}
                      </td>
                      <td className="text-left px-5 py-3 font-mono text-gray-800">
                        {formatSeconds(call.duration)}
                      </td>
                      <td className="text-left px-5 py-3 font-semibold text-gray-700">
                        {dirLabel}
                      </td>
                      <td className="text-left px-5 py-3">
                        {call.recording_s3_key ? (
                          <AudioPlayer callUuid={call.uuid} />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default TwilioAgent;
